import React from "react";
import { useLanguage } from "@/lib/LanguageContext";
import { dateLocale } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { CreditCard } from "lucide-react";

/**
 * TrialCard — the billing page's own statement of the trial, distinct from the date-free
 * shell banner in `AdminLayout.jsx` (which is on every page and cannot know the exact
 * date). `trial` is `billing/summary.trial` (the wire contract's read model, §2); the
 * frontend does no date arithmetic on it — every date shown here is the server's own
 * `ends_at` / `converted_at`, formatted for the reader's locale.
 *
 * `state == "none"` renders nothing: a practice with no trial history (or one that never
 * had a trial) has nothing to say about it here.
 */
export default function TrialCard({ trial, onManageCard, t }) {
  const { lang } = useLanguage();

  if (!trial || trial.state === "none") return null;

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(dateLocale(lang)) : "—");

  if (trial.state === "converted") {
    return (
      <div
        className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-eh-muted"
        data-testid="trial-card-converted"
      >
        <strong className="text-eh-ink">{t("bill.trial.convertedTitle")}</strong>{" "}
        {t("bill.trial.convertedOn", { date: fmtDate(trial.converted_at) })}
      </div>
    );
  }

  // `ended_early`: Stripe closed the trial before its recorded end, so `ends_at` is a date
  // still ahead and "ended on {ends_at}" would be false. The server says which case this
  // is; the card never compares the date with the clock to find out.
  if (trial.state === "ended") {
    return (
      <div
        className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-eh-muted"
        data-testid="trial-card-ended"
      >
        {trial.ended_early
          ? t("bill.trial.endedEarly", { date: fmtDate(trial.ends_at) })
          : t("bill.trial.endedOn", { date: fmtDate(trial.ends_at) })}
      </div>
    );
  }

  // `state == "trialing"` but the server sent no usable `days_remaining` (the invariant
  // says this should never happen, but a payload the frontend cannot trust to render a
  // day count is safer rendered as nothing than as "undefined days left").
  if (trial.state !== "trialing" || !Number.isInteger(trial.days_remaining)) return null;

  const days = trial.days_remaining;

  return (
    <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5" data-testid="trial-card">
      <div className="text-xs uppercase tracking-wider text-sky-700 mb-1">{t("bill.trial.title")}</div>
      <div className="font-sol font-semibold text-xl text-sky-900">
        {days === 0 ? t("bill.trial.lastDay") : t("bill.trial.daysLeft", { days })}
      </div>
      <div className="text-sm text-sky-800 mt-2">{t("bill.trial.endsOn", { date: fmtDate(trial.ends_at) })}</div>
      {/* No amount here on purpose: the invoice at day 30 charges whatever cycle the
          practice picked (monthly vs. annual), and this card cannot see which without
          duplicating a figure the plan card already owns — quoting one would misstate the
          other. */}
      <div className="text-sm text-sky-800 mt-1">{t("bill.trial.firstCharge", { date: fmtDate(trial.ends_at) })}</div>
      <Button onClick={onManageCard} data-testid="trial-portal-btn" size="sm" className="mt-3 bg-eh-ink text-eh-bg">
        <CreditCard className="w-4 h-4 mr-2" /> {t("bill.trial.manageCard")}
      </Button>
    </div>
  );
}
